(() => {
  const blocksGesture = (event) => {
    if (event.target.closest('input, select, textarea, label, audio, [contenteditable="true"]')) return true;
    const video = event.target.closest('video');
    return Boolean(video?.controls);
  };

  const bindPointerGesture = (element, onMove, onEnd) => {
    if (element.dataset.ulikePointerReady === 'true') return;
    element.dataset.ulikePointerReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let dragged = false;
    let preventClick = false;

    element.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0 || blocksGesture(event)) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      dragged = false;
      preventClick = false;
      if (event.pointerType === 'mouse' && !event.target.closest('a, button, video')) event.preventDefault();
    }, true);

    element.addEventListener('dragstart', (event) => {
      if (pointerId !== null) event.preventDefault();
    });

    element.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (!dragged && Math.abs(deltaX) < 5) return;
      if (!dragged && Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (!dragged) element.setPointerCapture?.(event.pointerId);
      if (event.cancelable) event.preventDefault();
      dragged = true;
      element.classList.add('is-pointer-dragging');
      onMove(deltaX, event);
    }, true);

    const finish = (event) => {
      if (pointerId === null || (event?.pointerId != null && event.pointerId !== pointerId)) return;
      const deltaX = event?.clientX != null ? event.clientX - startX : 0;
      const deltaY = event?.clientY != null ? event.clientY - startY : 0;
      const finishedPointerId = pointerId;
      preventClick = dragged;
      pointerId = null;
      dragged = false;
      element.classList.remove('is-pointer-dragging');
      if (element.hasPointerCapture?.(finishedPointerId)) element.releasePointerCapture(finishedPointerId);
      onEnd(deltaX, deltaY, event);
    };

    element.addEventListener('pointerup', finish);
    element.addEventListener('pointercancel', finish);
    element.addEventListener('lostpointercapture', event => {
      if (event.target === element) finish(event);
    });
    element.addEventListener('click', (event) => {
      if (!preventClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      preventClick = false;
    }, true);
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-ulike-drag-scroll]').forEach((element) => {
      element.style.touchAction = 'pan-y pinch-zoom';
      let startScrollLeft = 0;
      let startedAt = 0;
      let animation = 0;
      bindPointerGesture(element, (deltaX) => {
        if (!element.classList.contains('is-pointer-dragging')) startScrollLeft = element.scrollLeft;
        element.scrollLeft = startScrollLeft - deltaX;
      }, (deltaX, deltaY, event) => {
        const marqueeTrack = element.querySelector('[data-ulike-marquee-track]');
        if (marqueeTrack && element.classList.contains('is-manual')) {
          const marqueeGroup = marqueeTrack.firstElementChild;
          const travelDistance = marqueeGroup?.getBoundingClientRect().width || marqueeTrack.scrollWidth / 2;
          const durationValue = getComputedStyle(marqueeTrack).animationDuration.split(',')[0].trim();
          const duration = durationValue.endsWith('ms') ? parseFloat(durationValue) / 1000 : parseFloat(durationValue);
          const phase = travelDistance > 0 ? ((element.scrollLeft % travelDistance) + travelDistance) % travelDistance / travelDistance : 0;

          element.scrollLeft = 0;
          element.classList.remove('is-manual');
          marqueeTrack.style.transform = '';
          marqueeTrack.style.animation = '';
          marqueeTrack.style.animationDelay = Number.isFinite(duration) ? `${-phase * duration}s` : '';
        }

        if (!element.hasAttribute('data-ulike-drag-snap') || Math.abs(deltaX) < 5 || Math.abs(deltaX) <= Math.abs(deltaY) || event?.type !== 'pointerup') return;
        element.classList.add('is-pointer-dragging');
        const from = element.scrollLeft;
        const max = element.scrollWidth - element.clientWidth;
        const first = element.firstElementChild;
        const stops = [...new Set([...element.children].map(slide => Math.min(max, slide.offsetLeft - first.offsetLeft)).concat(max))];
        const quick = performance.now() - startedAt < 300;
        const target = quick
          ? (deltaX < 0 ? stops.find(stop => stop > from + 1) ?? max : stops.slice().reverse().find(stop => stop < from - 1) ?? 0)
          : stops.reduce((nearest, stop) => Math.abs(stop - from) < Math.abs(nearest - from) ? stop : nearest, 0);
        const began = performance.now();
        const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
        const settle = now => {
          const progress = duration ? Math.min(1, (now - began) / duration) : 1;
          element.scrollLeft = from + (target - from) * (1 - Math.pow(1 - progress, 3));
          if (progress < 1) animation = requestAnimationFrame(settle);
          else { animation = 0; element.classList.remove('is-pointer-dragging'); }
        };
        animation = requestAnimationFrame(settle);
      });
      element.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || blocksGesture(event)) return;
        cancelAnimationFrame(animation);
        animation = 0;
        startedAt = performance.now();
        const marqueeTrack = element.querySelector('[data-ulike-marquee-track]');
        if (marqueeTrack && !element.classList.contains('is-manual')) {
          const transform = getComputedStyle(marqueeTrack).transform;
          const values = transform !== 'none' ? transform.match(/matrix(?:3d)?\(([^)]+)\)/)?.[1].split(',').map(Number) : null;
          const translateX = values ? (values.length === 16 ? values[12] : values[4]) : 0;
          marqueeTrack.style.animation = 'none';
          marqueeTrack.style.transform = 'none';
          element.classList.add('is-manual');
          element.scrollLeft = Math.max(0, -translateX);
        }
        startScrollLeft = element.scrollLeft;
      }, true);
    });

    root.querySelectorAll('[data-ulike-drag-switch]').forEach((element) => {
      element.style.touchAction = 'pan-y pinch-zoom';
      bindPointerGesture(element, () => {}, (deltaX, deltaY, event) => {
        if (event?.type !== 'pointerup') return;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
        element.dispatchEvent(new CustomEvent('ulike:drag-switch', {
          bubbles: true,
          detail: { direction: deltaX < 0 ? 1 : -1 }
        }));
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
